import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { toZonedTime, fromZonedTime, formatInTimeZone } from 'date-fns-tz';
import { CalendarIcon, Clock, X, LogIn } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';

interface CountdownBannerProps {
  targetDate: Date | null;
  countdownEnabled: boolean;
  onDateChange?: (date: Date | null) => void;
  onToggleCountdown?: (enabled: boolean) => void;
  eventName?: string;
  isLoggedIn?: boolean;
  readOnly?: boolean;
}

interface TimeRemaining {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  isPast: boolean;
}

export const CountdownBanner = ({ 
  targetDate, 
  countdownEnabled, 
  onDateChange, 
  onToggleCountdown,
  eventName = "Event",
  isLoggedIn = false,
  readOnly = false
}: CountdownBannerProps) => {
  const [timeRemaining, setTimeRemaining] = useState<TimeRemaining | null>(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showEditDatePicker, setShowEditDatePicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(targetDate || undefined);
  const [selectedTime, setSelectedTime] = useState('07:00');
  const [selectedTimezone, setSelectedTimezone] = useState('America/Chicago');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!targetDate || !countdownEnabled) {
      setTimeRemaining(null);
      return;
    }

    const updateCountdown = () => {
      const now = new Date();
      const difference = targetDate.getTime() - now.getTime();
      
      if (difference < 0) {
        setTimeRemaining({
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0,
          isPast: true
        });
        return;
      }

      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      setTimeRemaining({ days, hours, minutes, seconds, isPast: false });
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [targetDate, countdownEnabled]);

  // Sync local state with prop changes
  useEffect(() => {
    if (targetDate) {
      setSelectedDate(targetDate);
      setSelectedTime(format(targetDate, 'HH:mm'));
    }
  }, [targetDate]);

  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      setSelectedDate(date);
      // Don't close the picker immediately, let user set time first
    }
  };

  const handleTimeChange = (time: string) => {
    setSelectedTime(time);
  };

  const handleTimezoneChange = (timezone: string) => {
    setSelectedTimezone(timezone);
  };

  const handleSaveDateTime = async () => {
    if (selectedDate) {
      setIsSaving(true);
      try {
        // Parse the time
        const [hours, minutes] = selectedTime.split(':').map(Number);
        
        // Create date in the selected timezone
        const localDate = new Date(selectedDate);
        localDate.setHours(hours, minutes, 0, 0);
        
        // Convert to UTC considering the timezone
        const utcDate = fromZonedTime(localDate, selectedTimezone);
        
        if (!isLoggedIn) {
          // Guest mode: save to sessionStorage
          sessionStorage.setItem('guest-countdown', JSON.stringify({
            targetDate: utcDate.toISOString(),
            enabled: true
          }));
        }
        
        onDateChange(utcDate);
        // Don't call onToggleCountdown separately, let the parent handle it
        setShowDatePicker(false);
        setShowEditDatePicker(false);
      } catch (error) {
        // Errors are logged in development only
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleRemoveCountdown = () => {
    onDateChange(null);
    onToggleCountdown(false);
  };

  if (!countdownEnabled || !targetDate) {
    return null;
  }

  return (
    <TooltipProvider>
      <div className="mb-4" role="region" aria-live="polite" aria-label="Event countdown timer">
      <Card className="border-border border-l-4 border-l-accent bg-card shadow-sm backdrop-blur-sm transition-colors duration-200 hover:border-accent" role="timer" aria-labelledby="countdown-heading">
        <CardContent className="p-3">
          <h3 id="countdown-heading" className="sr-only">Countdown to {eventName}</h3>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
            <div className="flex items-center gap-3">
              <Clock className="h-4 w-4 text-accent animate-pulse" aria-hidden="true" />
              {timeRemaining && !timeRemaining.isPast ? (
                <div className="flex gap-2 sm:gap-4" role="timer" aria-label={`${timeRemaining.days} days, ${timeRemaining.hours} hours, ${timeRemaining.minutes} minutes, ${timeRemaining.seconds} seconds until ${eventName}`}>
                  <div className="text-center">
                    <div className="font-semibold text-primary text-base sm:text-lg" aria-label={`${timeRemaining.days} days`}>{timeRemaining.days}</div>
                    <div className="text-xs text-muted-foreground">Days</div>
                  </div>
                  <div className="text-center">
                    <div className="font-semibold text-primary text-base sm:text-lg" aria-label={`${timeRemaining.hours} hours`}>{timeRemaining.hours}</div>
                    <div className="text-xs text-muted-foreground">Hours</div>
                  </div>
                  <div className="text-center">
                    <div className="font-semibold text-primary text-base sm:text-lg" aria-label={`${timeRemaining.minutes} minutes`}>{timeRemaining.minutes}</div>
                    <div className="text-xs text-muted-foreground">Minutes</div>
                  </div>
                  <div className="text-center">
                    <div className="font-semibold text-primary text-base sm:text-lg" aria-label={`${timeRemaining.seconds} seconds`}>{timeRemaining.seconds}</div>
                    <div className="text-xs text-muted-foreground">Seconds</div>
                  </div>
                </div>
              ) : timeRemaining?.isPast ? (
                <span className="text-sm text-muted-foreground">
                  Your event date has passed • {format(targetDate, 'PPP')}
                </span>
              ) : (
                <span className="text-sm text-muted-foreground">
                  {eventName} • {format(targetDate, 'PPP')}
                </span>
              )}
            </div>
            
            {!readOnly && (
              <div className="flex items-center gap-0 sm:gap-1">
                <TooltipProvider>
                  <Tooltip>
                    <Popover open={showEditDatePicker} onOpenChange={setShowEditDatePicker}>
                      <PopoverTrigger asChild>
                        <TooltipTrigger asChild>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="h-8 w-8 sm:h-11 sm:w-11 p-0 hover:bg-muted min-h-[32px] min-w-[32px] sm:min-h-[44px] sm:min-w-[44px]"
                            aria-label="Edit event date and time"
                          >
                            <CalendarIcon className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
                          </Button>
                        </TooltipTrigger>
                      </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <div className="p-4 space-y-4">
                      <CalendarComponent
                        mode="single"
                        selected={selectedDate}
                        onSelect={handleDateSelect}
                        disabled={(date) => date < new Date()}
                        initialFocus
                        defaultMonth={targetDate || new Date()}
                      />
                      {selectedDate && (
                        <div className="space-y-3 border-t pt-3">
                          <div className="space-y-2">
                            <Label htmlFor="edit-event-time" className="font-normal">Time</Label>
                            <Input
                              id="edit-event-time"
                              type="time"
                              value={selectedTime}
                              onChange={(e) => handleTimeChange(e.target.value)}
                              className="w-full font-normal"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="edit-event-timezone" className="font-normal">Timezone</Label>
                            <Select value={selectedTimezone} onValueChange={handleTimezoneChange}>
                              <SelectTrigger className="font-normal">
                                <SelectValue placeholder="Select timezone" />
                              </SelectTrigger>
                              <SelectContent className="font-normal">
                                <SelectItem value="America/New_York">Eastern Time</SelectItem>
                                <SelectItem value="America/Chicago">Central Time</SelectItem>
                                <SelectItem value="America/Denver">Mountain Time</SelectItem>
                                <SelectItem value="America/Los_Angeles">Pacific Time</SelectItem>
                                <SelectItem value="UTC">UTC</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <Button 
                            onClick={handleSaveDateTime} 
                            className="w-full font-normal bg-accent hover:bg-accent/90 text-accent-foreground"
                            disabled={isSaving}
                          >
                            {isSaving ? 'Saving...' : 'Update Event Date & Time'}
                          </Button>
                        </div>
                      )}
                    </div>
                  </PopoverContent>
                    </Popover>
                    <TooltipContent className="z-[100000] bg-popover text-popover-foreground border shadow-md">
                      <p>Edit event date and time</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                
                <TooltipProvider>
                  <Tooltip>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <TooltipTrigger asChild>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="h-8 w-8 sm:h-11 sm:w-11 p-0 hover:bg-destructive hover:text-destructive-foreground text-destructive min-h-[32px] min-w-[32px] sm:min-h-[44px] sm:min-w-[44px]"
                            aria-label="Remove countdown timer"
                          >
                            <X className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
                          </Button>
                        </TooltipTrigger>
                      </AlertDialogTrigger>
                   <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle className="font-normal">Remove Countdown Timer</AlertDialogTitle>
                      <AlertDialogDescription className="font-normal">
                        Are you sure you want to remove the countdown timer for {eventName}? This action cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="font-normal">Cancel</AlertDialogCancel>
                      <AlertDialogAction 
                        onClick={handleRemoveCountdown}
                        className="bg-accent hover:bg-accent/90 text-accent-foreground font-normal"
                      >
                        Remove Timer
                      </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                    </AlertDialog>
                    <TooltipContent className="z-[100000] bg-popover text-popover-foreground border shadow-md">
                      <p>Remove countdown timer</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      </div>
    </TooltipProvider>
  );
};